import React, { useEffect, useState, useContext } from "react";
import { useTranslation } from "react-i18next";
import { FiAlertCircle } from "react-icons/fi";
import { Controller, useFormContext } from "react-hook-form";
import { IoMdWarning } from "react-icons/io";
import { Switch } from "@/components/Switch";
import { Input } from "@/components/Input";
import { Alert } from "@/components/Alert";
import { QREditorContext } from "@/contexts/QREditorContext";
import getQRReferenceHandler from "@/interface/api/getQRReference";
import { FormValidationMethods } from "@/components/Form/types";
import { SwitchWithInputProps } from "./types";
import { QREditorContextProps } from "@/contexts/QREditorContext/types";
import { NAMESPACES } from "@/internals/constants/namespaces";
import { getSiteInfo } from "@/sites";
import { useDebounce } from "@/hooks/utils/useDebounce";
import { useApiWithLayout } from "@/hooks/api";
import { REFERENCE_EXITS } from "@/utils/qrEditorHelper";
import { SwitchWithInputContext } from "./context";

import "./styles.scss";

export const SwitchWithInput = ({
  label,
  checked,
  content,
  formFields,
  warning,
}: SwitchWithInputProps) => {
  const { t } = useTranslation(NAMESPACES.QR_EDITOR_STATS);

  const methods: FormValidationMethods = useFormContext();
  const { qrData, isQRfinished, setQrData } =
    useContext<QREditorContextProps<any>>(QREditorContext);

  const { qrCodeReference } = qrData;
  const { hasCustomReference } = qrData.data;

  const { domainName, scanDomain } = getSiteInfo(process.env.NEXT_PUBLIC_DOMAIN!) || {};
  const baseUrl = `https://www.${scanDomain || domainName}/p/`;

  const switchDefaultVal = hasCustomReference ? false : checked;

  const referenceInputValue =
    hasCustomReference && qrData.data.customReference
      ? qrData.data.customReference
      : qrCodeReference;

  const [switchVal, setSwitch] = useState(switchDefaultVal);
  const [inputVal, setInputVal] = useState(referenceInputValue || "");
  const [searchVal, setSearchVal] = useState("");

  const toggleSwitch = () => setSwitch((prevState) => !prevState);

  useEffect(() => {
    if (!switchVal) document.getElementById("customReference").focus();
    const copyQrData = { ...qrData.data };

    if (
      copyQrData.hasOwnProperty("hasCustomReference") &&
      copyQrData["hasCustomReference"] === true
    ) {
      delete copyQrData["hasCustomReference"];
      delete copyQrData["customeReference"];
    } else {
      copyQrData["hasCustomReference"] = true;
    }

    setQrData({
      ...qrData,
      data: {
        ...copyQrData,
      },
    });
  }, [switchVal]);

  const { request: requestQRReference, error } = useApiWithLayout(
    getQRReferenceHandler.getQRReference
  );

  const handleQRReferenceRequest = async (value: string) => {
    if (!value) return;
    await requestQRReference({ qrReference: value });
  };

  useEffect(() => {
    if (error?.data?.code === REFERENCE_EXITS)
      methods.setError("customReference", {
        message: t("reference_exist"),
      });
  }, [error]);

  const debounceValue = useDebounce(searchVal, 300);

  useEffect(() => {
    if (searchVal !== "" && /^[a-zA-Z0-9]+$/g.test(searchVal)) {
      handleQRReferenceRequest(searchVal);
    }
  }, [debounceValue]);

  const handleChange = (e) => {
    setInputVal(e);
    setSearchVal(e);
  };

  const hasError = methods.formState.errors["customReference"]?.message;

  return (
    <SwitchWithInputContext.Provider value={{ toggleSwitch, switchVal }}>
      <div className="switchWithInput">
        <Switch
          label={t(label)}
          content={t(content, { domain_name: domainName })}
        />
      </div>
      <>
        {!switchVal ? (
          <>
            <div className="Input__label-container">
              {formFields[0].label && (
                <label
                  data-testid="Input__label"
                  htmlFor={formFields[0].id}
                  className="Input__label"
                >
                  {formFields[0].label || ""}
                </label>
              )}
            </div>
            <div className={`input-mock ${hasError ? "hasError" : ""}`}>
              <span>{baseUrl}</span>

              <Controller
                control={methods?.control}
                name="customReference"
                render={() => (
                  <Input
                    disabled={switchVal}
                    id={formFields[0].id}
                    type="text"
                    placeholder={t("switch_label")}
                    name={formFields[0].name}
                    onChange={handleChange}
                    className="label-input__input"
                    error={methods.formState.errors["customReference"]}
                    errorMessage={methods.formState.errors["customReference"]?.message}
                    {...(checked && { value: inputVal })}
                  />
                )}
                rules={{
                  required: {
                    value: true,
                    message: t("required_message"),
                  },
                  maxLength: {
                    value: 36,
                    message: t("reference_max_length"),
                  },
                  pattern: {
                    value: new RegExp("^[a-zA-Z0-9]+$"),
                    message: t("reference_pattern"),
                  },
                }}
              />
            </div>
            {hasError && (
              <p
                data-testid="Input__error-message"
                className="Input__error-message_custom body-s"
              >
                <FiAlertCircle />
                {methods.formState.errors["customReference"]?.message as string}
              </p>
            )}
          </>
        ) : (
          <div className="input-mock single">
            {t(formFields[0].placeholder, { domain_name: scanDomain || domainName })}
          </div>
        )}

        {isQRfinished && warning && (
          <Alert
            icon={<IoMdWarning aria-label="warning-icon" />}
            text={t(warning)}
            type={"warning"}
            className="customReference__warning"
          />
        )}
      </>
    </SwitchWithInputContext.Provider>
  );
};
